import React, { useEffect, useState, useRef, Children, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SparkleEffect } from '../components/SparkleEffect';
import { GlitterHeart } from '../components/GlitterHeart';
import { HeroGraphic } from '../components/HeroGraphic';
import { BookOpenIcon, PlaneIcon, ArrowRightIcon, FlameIcon, HeartIcon, SparklesIcon } from 'lucide-react';
function NewsletterForm() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      const formData = new FormData();
      formData.append('fields[email]', email);
      formData.append('fields[name]', name);
      formData.append('ml-submit', '1');
      formData.append('anticsrf', 'true');
      await fetch('https://assets.mailerlite.com/jsonp/2211088/forms/182578891602266068/subscribe', {
        method: 'POST',
        body: formData,
        mode: 'no-cors'
      });
      setStatus('success');
      setEmail('');
      setName('');
    } catch {
      setStatus('error');
    }
  };
  if (status === 'success') {
    return <div className="bg-white/60 rounded-2xl p-6 text-center border border-plum-200/30" data-id="element-574">
        <p className="font-heading text-xl font-bold text-plum-700 mb-1" data-id="element-575">
          💖 You're in!
        </p>
        <p className="text-plum-500 text-sm font-medium" data-id="element-576">
          Real talk, course drops, and travel deals coming your way.
        </p>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-3" data-id="element-577">
      <div className="flex flex-col sm:flex-row gap-2" data-id="element-578">
        <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" className="flex-1 px-5 py-3.5 rounded-full border border-plum-200/50 bg-white/80 text-plum-700 text-sm font-medium placeholder:text-plum-400 focus:outline-none focus:ring-2 focus:ring-cta-400 focus:border-transparent" data-id="element-579" />
        <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Name" className="flex-1 px-5 py-3.5 rounded-full border border-plum-200/50 bg-white/80 text-plum-700 text-sm font-medium placeholder:text-plum-400 focus:outline-none focus:ring-2 focus:ring-cta-400 focus:border-transparent" data-id="element-580" />
      </div>
      <button type="submit" disabled={status === 'loading'} className="w-full px-7 py-3.5 rounded-full bg-cta-500 text-white font-bold text-sm hover:bg-cta-600 transition-colors shadow-md shadow-cta-500/20 whitespace-nowrap disabled:opacity-60" data-id="element-581">
        {status === 'loading' ? '...' : '💖 Get Notified, Gorgeous'}
      </button>
      {status === 'error' && <p className="text-red-500 text-xs font-medium" data-id="element-582">
          Something went wrong. Please try again.
        </p>}
    </form>;
}
export function HomePage() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const marqueePhrases = ['No toxic positivity', 'Real talk only', 'The Broken Beginning', 'Healing is messy', 'Take up space, babe'];
  return <div className="min-h-screen overflow-hidden" data-id="element-583">
      <style data-id="element-584">{`
        @keyframes marquee {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 25s linear infinite;
        }
      `}</style>

      {/* HERO SECTION - Split Layout */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden" data-id="element-585">
        {/* Dramatic Background Blobs */}
        <div className="absolute inset-0 bg-gradient-to-br from-blush-50 via-white to-lavender-50 -z-20" data-id="element-586" />
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blush-300/30 rounded-full blur-[120px] -z-10 -translate-x-1/2 -translate-y-1/4" data-id="element-587" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-lavender-300/30 rounded-full blur-[100px] -z-10 translate-x-1/3 translate-y-1/3" data-id="element-588" />
        <div className="absolute top-1/2 left-1/2 w-[800px] h-[400px] bg-cta-300/10 rounded-full blur-[100px] -z-10 -translate-x-1/2 -translate-y-1/2 rotate-45" data-id="element-589" />

        <SparkleEffect count={150} data-id="element-590" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-591">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center relative" data-id="element-592">
            {/* 100% Real Glassmorphism Card - Floating between text and heart */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            ease: 'easeOut',
            delay: 0.6
          }} className="hidden lg:block absolute left-[46%] top-8 z-30" data-id="element-593">
              <motion.div animate={{
              y: [8, -8, 8]
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="bg-white/70 backdrop-blur-xl border border-white p-4 rounded-2xl shadow-xl transform rotate-3 inline-flex items-center gap-3" data-id="element-594">
                <div className="w-9 h-9 rounded-full bg-cta-100 flex items-center justify-center" data-id="element-595">
                  <FlameIcon className="w-4 h-4 text-[#2D1533]" data-id="element-596" />
                </div>
                <div data-id="element-597">
                  <p className="font-heading text-lg font-bold text-plum-700 leading-tight" data-id="element-598">
                    100% Real
                  </p>
                  <p className="text-[10px] font-bold text-plum-500 uppercase tracking-wider" data-id="element-599">
                    No BS Allowed
                  </p>
                </div>
              </motion.div>
            </motion.div>

            {/* Left Content */}
            <motion.div variants={containerVariants} initial="hidden" animate="visible" className="text-center lg:text-left pt-10" data-id="element-600">
              <motion.div variants={itemVariants} className="mb-8 inline-block" data-id="element-601">
                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/80 backdrop-blur-md border border-blush-300 text-sm font-bold text-plum-700 shadow-lg shadow-blush-200/50 uppercase tracking-wider transform -rotate-2" data-id="element-602">
                  <GlitterHeart size={18} data-id="element-603" />
                  Real talk. Real healing. Real results.
                </span>
              </motion.div>

              <motion.h1 variants={itemVariants} className="font-heading text-6xl md:text-8xl font-bold mb-6 tracking-tight leading-[1.1]" data-id="element-604">
                <span className="block pb-2 text-plum-700" data-id="element-605">Trippin'</span>
                <span className="block text-[#D63385] text-[56px]" data-id="element-606">
                  Through Life
                </span>
                <span className="md:text-6xl block mt-2 text-[#2D1533] text-[80px]" data-id="element-607">
                  with Mandy
                </span>
              </motion.h1>

              <motion.p variants={itemVariants} className="text-xl md:text-2xl text-plum-500 max-w-xl mx-auto lg:mx-0 mb-10 font-medium leading-relaxed" data-id="element-608">
                I turned my mess into my message—and now I help you through The
                Broken Beginning.
              </motion.p>

              <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-5" data-id="element-609">
                <Link to="/coaching" className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-cta-500 text-white font-bold text-lg hover:bg-cta-600 transition-all shadow-xl shadow-cta-500/30 hover:-translate-y-1" data-id="element-610">
                  <GlitterHeart size={20} variant="light" data-id="element-611" />
                  Start Your Transformation
                </Link>
                <Link to="/courses" className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-cta-500 text-white font-bold text-lg hover:bg-cta-600 transition-all shadow-xl shadow-cta-500/30 hover:-translate-y-1" data-id="element-612">
                  <GlitterHeart size={20} variant="light" data-id="element-613" />
                  Explore My Programs
                </Link>
                <a href="https://beacons.ai/trippinwithmandy" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-cta-500 text-white font-bold text-lg hover:bg-cta-600 transition-all shadow-xl shadow-cta-500/30 hover:-translate-y-1" data-id="element-614">
                  <GlitterHeart size={20} variant="light" data-id="element-615" />
                  Shop My Storefront
                </a>
              </motion.div>
            </motion.div>

            {/* Right Decorative Composition */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.85
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1.2,
            ease: 'easeOut',
            delay: 0.2
          }} className="relative hidden lg:flex justify-center items-center h-[600px]" data-id="element-616">
              <HeroGraphic data-id="element-617" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* SASSY MARQUEE */}
      <div className="relative py-6 bg-plum-900 overflow-hidden transform -rotate-1 scale-105 shadow-2xl z-20" data-id="element-618">
        <div className="absolute inset-0 bg-gradient-to-r from-cta-500/20 to-plum-500/20 mix-blend-overlay" data-id="element-619" />
        <div className="flex whitespace-nowrap animate-marquee" data-id="element-620">
          {/* Double the array for seamless looping */}
          {[...marqueePhrases, ...marqueePhrases, ...marqueePhrases, ...marqueePhrases].map((phrase, i) => <div key={i} className="flex items-center gap-8 mx-8" data-id="element-621">
              <span className="font-heading text-2xl md:text-3xl font-bold text-white tracking-wide" data-id="element-622">
                {phrase}
              </span>
              <GlitterHeart size={24} variant="light" data-id="element-623" />
            </div>)}
        </div>
      </div>

      {/* WHAT I OFFER - Bento Grid */}
      <section className="py-32 bg-blush-50 relative" data-id="element-624">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white rounded-full blur-[100px] -z-10 translate-x-1/2 -translate-y-1/2" data-id="element-625" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-626">
          <div className="text-center mb-20" data-id="element-627">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="inline-flex items-center justify-center gap-3 mb-6" data-id="element-628">
              <GlitterHeart size={24} data-id="element-629" />
              <h2 className="font-heading text-5xl md:text-6xl font-bold text-plum-700" data-id="element-630">
                How We're Doing This
              </h2>
              <GlitterHeart size={24} data-id="element-631" />
            </motion.div>
            <p className="text-plum-500 max-w-2xl mx-auto text-xl font-medium" data-id="element-632">
              No fluff. No toxic positivity. Just real tools to help you stop
              surviving and start thriving.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} className="grid grid-cols-1 lg:grid-cols-3 gap-6 auto-rows-[minmax(250px,auto)]" data-id="element-633">
            {/* Coaching Card - FEATURED (Spans 2 cols on desktop) */}
            <motion.div variants={itemVariants} className="lg:col-span-2 lg:row-span-2 group relative bg-gradient-to-br from-plum-900 via-plum-800 to-plum-900 p-10 md:p-14 rounded-[3rem] shadow-2xl overflow-hidden flex flex-col justify-between" data-id="element-634">
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-cta-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-635" />
              <div className="absolute bottom-0 left-0 w-80 h-80 bg-lavender-500/20 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3" data-id="element-636" />
              <SparkleEffect count={40} data-id="element-637" />

              <div className="absolute top-8 right-8 opacity-20 group-hover:opacity-40 transition-opacity duration-500 group-hover:scale-110 transform" data-id="element-638">
                <GlitterHeart size={120} variant="light" data-id="element-639" />
              </div>

              <div className="relative z-10 mb-12" data-id="element-640">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-bold mb-8 uppercase tracking-wider" data-id="element-641">
                  <FlameIcon className="w-4 h-4 text-cta-400" data-id="element-642" />
                  Signature Experience
                </div>
                <h3 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-643">
                  1:1 Coaching
                </h3>
                <p className="text-plum-100 text-xl max-w-md leading-relaxed font-medium" data-id="element-644">
                  Personalized, no-BS coaching to help you stop surviving and
                  start thriving. We dig deep and do the real work.
                </p>
              </div>

              <div className="relative z-10 mt-auto" data-id="element-645">
                <Link to="/coaching" className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-cta-500 text-white font-bold text-lg hover:bg-cta-400 transition-all shadow-lg hover:-translate-y-1 w-full sm:w-auto" data-id="element-646">
                  <GlitterHeart size={20} variant="light" data-id="element-647" />
                  Work With Me
                  <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-648" />
                </Link>
              </div>
            </motion.div>

            {/* Courses Card */}
            <motion.div variants={itemVariants} className="group relative bg-gradient-to-br from-white to-blush-50 p-8 md:p-10 rounded-[3rem] shadow-xl border border-blush-200 overflow-hidden flex flex-col justify-between hover:-translate-y-2 transition-transform duration-500" data-id="element-649">
              <div className="absolute -right-6 -top-6 opacity-10 group-hover:opacity-20 transition-opacity duration-500" data-id="element-650">
                <BookOpenIcon className="w-40 h-40 text-cta-500" data-id="element-651" />
              </div>

              <div className="relative z-10 mb-8" data-id="element-652">
                <div className="w-16 h-16 rounded-2xl bg-blush-200 flex items-center justify-center mb-6 shadow-inner" data-id="element-653">
                  <BookOpenIcon className="w-8 h-8 text-plum-700" data-id="element-654" />
                </div>
                <h3 className="font-heading text-3xl font-bold text-plum-700 mb-4" data-id="element-655">
                  The Broken Beginning
                </h3>
                <p className="text-plum-600 font-medium leading-relaxed" data-id="element-656">
                  The Broken Beginning and beyond. Self-paced courses that don't
                  sugarcoat the journey.
                </p>
              </div>

              <Link to="/courses" className="relative z-10 inline-flex items-center font-bold text-cta-500 hover:text-cta-600 text-lg mt-auto" data-id="element-657">
                <GlitterHeart size={18} className="mr-2" data-id="element-658" />
                Get Started
                <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" data-id="element-659" />
              </Link>
            </motion.div>

            {/* Travel Card */}
            <motion.div variants={itemVariants} className="group relative bg-gradient-to-br from-white to-sky-50 p-8 md:p-10 rounded-[3rem] shadow-xl border border-sky-200 overflow-hidden flex flex-col justify-between hover:-translate-y-2 transition-transform duration-500" data-id="element-660">
              <div className="absolute -right-4 -bottom-4 opacity-10 group-hover:opacity-20 transition-opacity duration-500" data-id="element-661">
                <PlaneIcon className="w-40 h-40 text-sky-500" data-id="element-662" />
              </div>

              <div className="relative z-10 mb-8" data-id="element-663">
                <div className="w-16 h-16 rounded-2xl bg-sky-200 flex items-center justify-center mb-6 shadow-inner" data-id="element-664">
                  <PlaneIcon className="w-8 h-8 text-plum-700" data-id="element-665" />
                </div>
                <h3 className="font-heading text-3xl font-bold text-plum-700 mb-4" data-id="element-666">
                  Travel
                </h3>
                <p className="text-plum-600 font-medium leading-relaxed" data-id="element-667">
                  Budget-friendly getaways because everyone deserves to escape,
                  reset, and explore.
                </p>
              </div>

              <Link to="/travel" className="relative z-10 inline-flex items-center font-bold text-cta-500 hover:text-cta-600 text-lg mt-auto" data-id="element-668">
                <GlitterHeart size={18} className="mr-2" data-id="element-669" />
                Plan a Trip
                <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" data-id="element-670" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* DRAMATIC QUOTE SECTION */}
      <section className="py-32 bg-gradient-to-br from-plum-900 via-plum-800 to-plum-900 relative overflow-hidden" data-id="element-671">
        <SparkleEffect count={120} data-id="element-672" />

        {/* Decorative giant quotes */}
        <div className="absolute top-10 left-10 text-[20rem] font-heading text-white/5 leading-none select-none pointer-events-none" data-id="element-673">
          "
        </div>
        <div className="absolute bottom-0 right-10 text-[20rem] font-heading text-white/5 leading-none select-none pointer-events-none rotate-180" data-id="element-674">
          "
        </div>

        <div className="max-w-5xl mx-auto px-4 text-center relative z-10" data-id="element-675">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1,
          ease: 'easeOut'
        }} className="flex flex-col items-center" data-id="element-676">
            <div className="flex items-center justify-center gap-6 mb-12" data-id="element-677">
              <GlitterHeart size={48} variant="light" className="animate-pulse" data-id="element-678" />
              <GlitterHeart size={64} variant="light" data-id="element-679" />
              <GlitterHeart size={48} variant="light" className="animate-pulse" data-id="element-680" />
            </div>

            <blockquote className="font-heading text-4xl md:text-6xl lg:text-7xl text-white leading-tight mb-12 drop-shadow-xl" data-id="element-681">
              "You didn't survive all that shit just to live small. It's time to
              <span className="text-cta-400 block mt-4" data-id="element-682">
                take up space, babe.
              </span>
              "
            </blockquote>

            <div className="w-24 h-1.5 bg-gradient-to-r from-transparent via-cta-400 to-transparent rounded-full" data-id="element-683" />
          </motion.div>
        </div>
      </section>

      {/* NEWSLETTER - MailerLite Embedded Form */}
      <section className="py-32 bg-gradient-to-b from-blush-50 to-white relative overflow-hidden" data-id="element-684">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-[400px] bg-cta-300/20 rounded-full blur-[120px] -z-10" data-id="element-685" />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-686">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="bg-[#f5d8d8]/80 backdrop-blur-md rounded-2xl p-8 md:p-12 text-center relative shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/40" data-id="element-687">
            {/* Floating decorative hearts */}
            <div className="absolute -top-6 -left-6 rotate-12" data-id="element-688">
              <GlitterHeart size={60} data-id="element-689" />
            </div>
            <div className="absolute -bottom-8 -right-8 -rotate-12" data-id="element-690">
              <GlitterHeart size={80} data-id="element-691" />
            </div>

            <div className="relative z-10 max-w-xl mx-auto" data-id="element-692">
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-plum-800 mb-4" data-id="element-693">
                Stay Connected
              </h2>
              <p className="text-plum-700 text-lg mb-8 font-medium leading-relaxed" data-id="element-694">
                Get real talk, course drops, and travel deals straight to your
                inbox.
              </p>

              {/* MailerLite Newsletter Form */}
              <NewsletterForm data-id="element-695" />

              <div className="mt-8 pt-6 border-t border-plum-200/30" data-id="element-696">
                <a href="https://calendly.com/trippinwithmandy/free-consultation-call" target="_blank" rel="noopener noreferrer" className="inline-block text-plum-700 font-medium hover:text-plum-900 transition-colors relative group" data-id="element-697">
                  Not ready yet? Book a free consultation with me 💖
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-plum-400 scale-x-0 group-hover:scale-x-100 transition-transform origin-left rounded-full" data-id="element-698"></span>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}